import React, { useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { supabase } from '../../supabase';
import { X, User, Store, Truck, Timer, MapPin, Banknote, QrCode, CheckCircle, UploadCloud, FileText, Phone } from 'lucide-react';
import { initMercadoPago, Wallet } from '@mercadopago/sdk-react';
import { syncStoreOrderClientAccount } from '../../utils/storeOrderAccounting';
import { getStoreLogisticsConfig } from '../../utils/storeLogistics';
import { buildProductDisplayName } from '../../utils/productLabels';
import { getLowStockAlertConfig, getLowStockCartItems } from '../../utils/storeLowStockAlert';
import LowStockNotice from './LowStockNotice';
import {
    formatPaymentConditionLabel,
    getClientPaymentConditionDays,
    getSaleReceivableDueDate,
} from '../../utils/salesReceivables';

const formatCurrency = (amount) => new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', minimumFractionDigits: 0 }).format(amount);
const normalizePhone = (value) => String(value || '').replace(/\D/g, '');
const normalizeDocument = (value) => String(value || '').replace(/\D/g, '');
const inferDocumentType = (value) => {
    const digits = normalizeDocument(value);
    if (digits.length >= 11) return 'CUIT';
    if (digits.length >= 7) return 'DNI';
    return 'DNI';
};

const withAlpha = (color, alphaHex) => {
    if (typeof color !== 'string') return color;
    const value = color.trim();
    if (/^#([0-9a-fA-F]{6})$/.test(value)) return `${value}${alphaHex}`;
    if (/^#([0-9a-fA-F]{3})$/.test(value)) {
        const [r, g, b] = value.slice(1).split('');
        return `#${r}${r}${g}${g}${b}${b}${alphaHex}`;
    }
    return value;
};

const getLocalToday = () => {
    const now = new Date();
    const offsetMs = now.getTimezoneOffset() * 60000;
    const localDate = new Date(now.getTime() - offsetMs);
    return localDate.toISOString().split('T')[0];
};

const getForgedPOSDate = () => {
    const now = new Date();
    const datePart = getLocalToday();
    const [y, m, d] = datePart.split('-').map(Number);
    const hh = String(now.getHours()).padStart(2, '0');
    const mm = String(now.getMinutes()).padStart(2, '0');
    const ss = String(now.getSeconds()).padStart(2, '0');
    return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}T${hh}:${mm}:${ss}.000Z`;
};

export default function CheckoutModal({ onClose, cartItems, orgId, config, siteId, enabledPaymentMethods = [], onSuccess, primaryColor, showPrices = true, onClientResolved }) {
    const [step, setStep] = useState(0);
    const [processing, setProcessing] = useState(false);
    const [mpPreferenceId, setMpPreferenceId] = useState(null);
    const [currentOrderTracking, setCurrentOrderTracking] = useState('');
    const [authIdentifier, setAuthIdentifier] = useState('');
    const [contactPhone, setContactPhone] = useState('');
    const [stepError, setStepError] = useState('');
    const [clientName, setClientName] = useState('');
    const [client, setClient] = useState(null);
    const [matchedClientCandidate, setMatchedClientCandidate] = useState(null);
    const [deliveryType, setDeliveryType] = useState('retiro');
    const [shippingData, setShippingData] = useState({ address: '', city: '', notes: '', requestedDate: '' });
    const [paymentMethod, setPaymentMethod] = useState('');
    const [transferFile, setTransferFile] = useState(null);
    const deliveryFormRef = useRef(null);

    const subtotal = cartItems.reduce((acc, item) => acc + (item.price * item.qty), 0);
    const shippingCost = deliveryType === 'envio' ? (subtotal >= (config.free_shipping_threshold || 999999999) ? 0 : (config.shipping_cost || 0)) : 0;
    const total = Number(subtotal) + Number(shippingCost);

    const defaultMethods = [
        { id: 'Efectivo', name: 'Efectivo', display_name: 'Efectivo' },
        { id: 'Transferencia', name: 'Transferencia', display_name: 'Transferencia' }
    ];
    const availableMethods = enabledPaymentMethods.length > 0 ? enabledPaymentMethods : defaultMethods;
    const theme = config?.theme_config || {};
    const logisticsConfig = getStoreLogisticsConfig(config);
    const accent = primaryColor || theme.primaryColor || '#0f172a';
    const allowCheckoutWithoutStock = Boolean(theme.allow_checkout_without_stock);
    // Aviso de ultimas unidades en el paso de pago: informa, no bloquea.
    const lowStockAlertConfig = getLowStockAlertConfig(config);
    const lowStockItems = getLowStockCartItems(cartItems, lowStockAlertConfig);
    const requestDateEnabled = Boolean(logisticsConfig.request_date_enabled);
    const modalBg = theme.modalColors?.bg || '#ffffff';
    const modalBorder = theme.modalColors?.border || '#e5e7eb';
    const modalText = theme.modalColors?.text || '#111827';
    const identityMode = theme.checkoutIdentityMode === 'document' ? 'document' : 'phone';
    const accentSoft = withAlpha(accent, '14');
    const accentSoftBorder = withAlpha(accent, '33');
    const readDeliveryFormValues = () => {
        const form = deliveryFormRef.current;

        return {
            nextName: String(form?.elements?.client_name?.value || clientName || '').trim(),
            nextPhone: String(form?.elements?.contact_phone?.value || contactPhone || '').trim(),
            nextAddress: String(form?.elements?.shipping_address?.value || shippingData.address || '').trim(),
            nextCity: String(form?.elements?.shipping_city?.value || shippingData.city || '').trim(),
            nextNotes: String(form?.elements?.shipping_notes?.value || shippingData.notes || '').trim(),
            nextRequestedDate: String(form?.elements?.requested_date?.value || shippingData.requestedDate || '').trim(),
        };
    };

    const applyMatchedClient = (matchedClient) => {
        if (!matchedClient) return;
        setClient(matchedClient);
        setMatchedClientCandidate(null);
        setClientName(matchedClient.full_name || '');
        setContactPhone(normalizePhone(matchedClient.phone || ''));
        onClientResolved?.(matchedClient);
        setShippingData((prev) => ({
            ...prev,
            address: matchedClient.address || prev.address || '',
            city: matchedClient.city || prev.city || '',
        }));
    };

    const resetDocumentCandidate = () => {
        setMatchedClientCandidate(null);
        setClient(null);
        setClientName('');
        setContactPhone('');
        setStepError('');
        onClientResolved?.(null);
    };

    const handleConfirmMatchedClient = () => {
        if (!matchedClientCandidate) return;
        applyMatchedClient(matchedClientCandidate);
        toast.success(`Hola de nuevo, ${(matchedClientCandidate.full_name || 'Cliente').split(' ')[0]}!`);
        setStep(1);
    };

    const handleRetryDocument = () => {
        resetDocumentCandidate();
        setAuthIdentifier('');
        toast('Ingresa nuevamente tu CUIT o DNI.');
    };

    const handleCreateNewDocumentAccount = () => {
        setMatchedClientCandidate(null);
        setClient({ isNew: true, forceNewWithoutDocument: true });
        setClientName('');
        setContactPhone('');
        setStepError('');
        onClientResolved?.(null);
        setStep(1);
        toast('Completa tus datos para crear una cuenta nueva.');
    };

    const handleIdentify = async (e) => {
        e.preventDefault();
        const rawIdentifier = e.currentTarget.elements.auth_identifier?.value || authIdentifier || '';
        const submittedIdentifier = identityMode === 'document'
            ? normalizeDocument(rawIdentifier)
            : normalizePhone(rawIdentifier);

        if (identityMode === 'document' && submittedIdentifier.length < 7) {
            setStepError('Ingresa un CUIT o DNI valido para continuar.');
            toast.error('Ingresa un CUIT o DNI valido');
            return;
        }

        if (identityMode === 'phone' && submittedIdentifier.length < 8) {
            setStepError('Ingresa un telefono valido para continuar.');
            toast.error('Ingresa un telefono valido');
            return;
        }

        setStepError('');
        setAuthIdentifier(submittedIdentifier);
        setMatchedClientCandidate(null);
        setProcessing(true);
        try {
            const query = supabase
                .from('clients')
                .select('*')
                .eq('organization_id', orgId);

            const { data, error } = identityMode === 'document'
                ? await query.not('doc_number', 'is', null)
                : await query.not('phone', 'is', null);

            if (error) throw error;

            const matchedClient = (data || []).find((row) => {
                if (identityMode === 'document') {
                    return normalizeDocument(row.doc_number) === submittedIdentifier;
                }

                const storedPhone = normalizePhone(row.phone);
                if (!storedPhone) return false;
                return storedPhone === submittedIdentifier
                    || storedPhone.endsWith(submittedIdentifier)
                    || submittedIdentifier.endsWith(storedPhone);
            });

            if (matchedClient) {
                if (identityMode === 'document') {
                    resetDocumentCandidate();
                    setMatchedClientCandidate(matchedClient);
                    toast.success('Encontramos una cuenta asociada a ese documento.');
                    return;
                }

                applyMatchedClient(matchedClient);
                toast.success(`Hola de nuevo, ${(matchedClient.full_name || 'Cliente').split(' ')[0]}!`);
            } else {
                setClient({ isNew: true });
                setClientName('');
                setContactPhone(identityMode === 'document' ? '' : submittedIdentifier);
                onClientResolved?.(null);
                toast('Parece que es tu primera vez aqui. Completa tus datos.');
            }

            setStep(1);
        } catch (err) {
            console.error(err);
            toast.error('Error al verificar identidad');
        } finally {
            setProcessing(false);
        }
    };
    const uploadProof = async (orderCode) => {
        if (!transferFile) return null;
        try {
            const fileExt = transferFile.name.split('.').pop();
            const fileName = `${orderCode}_proof.${fileExt}`;

            const { error: uploadError } = await supabase.storage
                .from('comprobantes')
                .upload(fileName, transferFile);

            if (uploadError) throw uploadError;

            const { data: { publicUrl } } = supabase.storage
                .from('comprobantes')
                .getPublicUrl(fileName);

            return publicUrl;
        } catch (e) {
            console.error("Error subiendo comprobante", e);
            toast.error("No se pudo subir la imagen (verifica permisos/internet).");
            return null;
        }
    };

    const handleFinish = async () => {
        setProcessing(true);

        if (!paymentMethod) {
            toast.error('Por favor, selecciona un metodo de pago.');
            setProcessing(false);
            return;
        }

        const methodObj = availableMethods.find(m => m.id === paymentMethod);
        const methodName = methodObj ? methodObj.name : paymentMethod;
        const isTransfer = methodName.toLowerCase().includes('transferencia');

        if (isTransfer && !transferFile) {
            toast.error('Por favor, adjunta el comprobante de transferencia.', { duration: 4000, icon: '!' });
            setProcessing(false);
            return;
        }

        try {
            let finalClientId = client?.id;
            const normalizedDocument = identityMode === 'document' ? normalizeDocument(authIdentifier) : '';
            const orderContactPhone = identityMode === 'document'
                ? normalizePhone(contactPhone)
                : normalizePhone(authIdentifier);
            const shouldPersistDocument = identityMode === 'document' && normalizedDocument && !client?.forceNewWithoutDocument;
            const documentType = shouldPersistDocument ? inferDocumentType(normalizedDocument) : null;
            const documentNumber = shouldPersistDocument ? normalizedDocument : null;

            const clientPayload = {
                organization_id: orgId,
                full_name: clientName || 'Cliente Web',
                email: null,
                phone: orderContactPhone || null,
                address: shippingData.address,
                city: shippingData.city,
                is_online_client: true,
                tax_condition: 'Consumidor Final',
                payment_condition_days: 0,
                default_payment_condition: 'Contado',
                doc_type: documentType,
                doc_number: documentNumber,
            };

            if (client?.isNew) {
                const { data: newClient, error: clientErr } = await supabase.from('clients').insert(clientPayload).select().single();
                if (clientErr) throw clientErr;
                finalClientId = newClient.id;
            } else {
                const clientUpdate = {
                    address: shippingData.address,
                    city: shippingData.city,
                    phone: orderContactPhone || null,
                };

                if (identityMode === 'document' && documentType && documentNumber && !client?.doc_number) {
                    clientUpdate.doc_type = documentType;
                    clientUpdate.doc_number = documentNumber;
                }

                await supabase.from('clients').update(clientUpdate).eq('id', finalClientId);
            }

            const createdAt = getForgedPOSDate();
            const receivableClient = client?.isNew
                ? clientPayload
                : {
                    ...client,
                    phone: orderContactPhone || null,
                    doc_type: client?.doc_type || documentType,
                    doc_number: client?.doc_number || documentNumber,
                };
            const paymentConditionDays = getClientPaymentConditionDays(receivableClient);
            const paymentConditionLabel = formatPaymentConditionLabel(paymentConditionDays);
            const paymentDueDate = getSaleReceivableDueDate({
                createdAt,
                client: {
                    ...receivableClient,
                    payment_condition_days: paymentConditionDays,
                    default_payment_condition: paymentConditionLabel,
                },
                paymentConditionDays,
            });

            const { data: sale, error: saleError } = await supabase
                .from('sales')
                .insert({
                    organization_id: orgId,
                    client_id: finalClientId,
                    total: total,
                    status: 'pending',
                    payment_method: methodName,
                    billing_source: 'tienda_online',
                    invoice_status: 'pendiente',
                    active: true,
                    site_id: siteId || null,
                    created_at: createdAt
                })
                .select()
                .single();

            if (saleError) throw saleError;

            const itemsToInsert = cartItems.map(item => ({
                sale_id: sale.id,
                organization_id: orgId,
                product_id: item.id,
                variant_id: item.variantId || null,
                product_name_snapshot: buildProductDisplayName(item.name, item.variantName),
                quantity: item.qty,
                unit_price: item.price,
                subtotal: item.price * item.qty
            }));

            if (deliveryType === 'envio' && shippingCost > 0) {
                itemsToInsert.push({
                    sale_id: sale.id,
                    organization_id: orgId,
                    product_id: null,
                    product_name_snapshot: 'Costo de Envio',
                    quantity: 1,
                    unit_price: shippingCost,
                    subtotal: shippingCost
                });
            }

            await supabase.from('sale_items').insert(itemsToInsert);

            if (siteId) {
                for (const item of cartItems) {
                    const { data: st } = await supabase
                        .from('stock_levels')
                        .select('id, quantity')
                        .eq('product_id', item.id)
                        .eq('site_id', siteId)
                        .maybeSingle();

                    const currentQty = Number(st?.quantity) || 0;
                    const newQty = allowCheckoutWithoutStock
                        ? currentQty - Number(item.qty)
                        : Math.max(0, currentQty - Number(item.qty));

                    if (st?.id) {
                        await supabase.from('stock_levels').update({ quantity: newQty, updated_at: new Date() }).eq('id', st.id);
                    } else {
                        await supabase.from('stock_levels').insert({ organization_id: orgId, product_id: item.id, site_id: siteId || null, quantity: newQty });
                    }

                    await supabase.from('movimientos_stock').insert({
                        organization_id: orgId,
                        producto_id: item.id,
                        tipo: 'Egreso',
                        cantidad: Number(item.qty),
                        motivo: `Venta Online #${sale.id.slice(0, 6)}`,
                        sitio_origen: siteId
                    });
                }
            }

            const trackingCode = `ORD-${sale.id.slice(0, 4).toUpperCase()}-${Math.floor(1000 + Math.random() * 9000)}`;

            let proofUrl = null;
            if (isTransfer && transferFile) {
                proofUrl = await uploadProof(trackingCode);
            }

            const notesArray = cartItems.map(i => i.note).filter(Boolean);
            if (proofUrl) {
                notesArray.push(`COMPROBANTE: ${proofUrl}`);
            }

            const finalShippingData = deliveryType === 'envio'
                ? { ...shippingData, proof_url: proofUrl, requested_date: shippingData.requestedDate || null, order_origin: 'web' }
                : { proof_url: proofUrl, pickup_person: clientName, requested_date: shippingData.requestedDate || null, order_origin: 'web' };

            const { error: storeOrderError } = await supabase
                .from('store_orders')
                .insert({
                    organization_id: orgId,
                    sale_id: sale.id,
                    store_slug: (config.slug || '').toLowerCase(),
                    tracking_code: trackingCode,
                    delivery_type: deliveryType,
                    shipping_cost: shippingCost,
                    shipping_address: finalShippingData,
                    customer_snapshot: {
                        name: clientName,
                        contact: orderContactPhone || documentNumber || authIdentifier,
                        phone: orderContactPhone || null,
                        doc_type: documentType,
                        doc_number: documentNumber,
                        requested_date: shippingData.requestedDate || null,
                        order_origin: 'web',
                    },
                    customer_phone: orderContactPhone || null,
                    status: 'recibido',
                    notes: notesArray.join('. ')
                });

            if (storeOrderError) throw storeOrderError;

            await syncStoreOrderClientAccount({
                orderId: sale.id,
                saleId: sale.id,
                organizationId: orgId,
                trackingCode,
                nextStatus: 'recibido',
                shippingCost,
                orderOrigin: 'web',
            });

            if (isTransfer && deliveryType === 'envio' && shippingCost > 0 && config.shipping_provider_id) {
                try {
                    await supabase.from('gastos').insert({
                        organization_id: orgId,
                        concepto: `Envio Pedido #${trackingCode}`,
                        monto: shippingCost,
                        fecha: new Date(),
                        categoria: 'Variable',
                        observaciones: 'Generado automaticamente desde Tienda Online'
                    });

                    const { data: prov } = await supabase
                        .from('proveedores')
                        .select('saldo')
                        .eq('id', config.shipping_provider_id)
                        .single();

                    const currentBalance = Number(prov?.saldo) || 0;
                    const newBalance = currentBalance + shippingCost;

                    await supabase
                        .from('proveedores')
                        .update({ saldo: newBalance })
                        .eq('id', config.shipping_provider_id);

                    await supabase.from('proveedor_movimientos').insert({
                        organization_id: orgId,
                        proveedor_id: config.shipping_provider_id,
                        tipo: 'Gasto Envio',
                        monto: shippingCost,
                        saldo_resultante: newBalance,
                        fecha: new Date()
                    });

                } catch (shippingError) {
                    console.error('Error registrando gasto de envio:', shippingError);
                }
            }

            if (methodObj && methodObj.gateway_type === 'MERCADOPAGO') {
                const { data: orgData } = await supabase.from('organizations').select('*').eq('id', orgId).single();
                if (orgData && orgData.mp_public_key) {
                    initMercadoPago(orgData.mp_public_key, { locale: 'es-AR' });

                    const mpItems = cartItems.map(item => ({
                        title: buildProductDisplayName(item.name, item.variantName),
                        quantity: item.qty,
                        unit_price: Number(item.price),
                    }));

                    if (deliveryType === 'envio' && shippingCost > 0) {
                        mpItems.push({
                            title: 'Costo de Envio',
                            quantity: 1,
                            unit_price: Number(shippingCost)
                        });
                    }

                    const edgePayload = {
                        orderId: sale.id,
                        organizationId: orgId,
                        items: mpItems,
                        payer: { name: clientName, email: client?.email || 'cliente@impulza.com' },
                        // Para que Mercado Pago vuelva al rastreo del pedido.
                        trackingCode,
                    };

                    const { data: edgeData, error: edgeError } = await supabase.functions.invoke('mp-create-preference', { body: edgePayload });

                    if (edgeData && edgeData.preferenceId) {
                        setMpPreferenceId(edgeData.preferenceId);
                        setCurrentOrderTracking(trackingCode);
                        setProcessing(false);
                        return;
                    } else {
                        console.error('Edge Error:', edgeError || edgeData);
                        toast.error('Error generando link de pago. Intenta otro metodo o mas tarde.');
                        setProcessing(false);
                        return;
                    }
                } else {
                    toast.error('Falta la Public Key de Mercado Pago en la configuracion de la empresa.');
                    setProcessing(false);
                    return;
                }
            }

            if (methodObj && methodObj.gateway_type === 'GETNET') {
                // Verificamos getnet_configured via RPC SECURITY DEFINER (no expone el secret).
                let getnetConfigured = false;
                const { data: payCtx, error: payCtxError } = await supabase
                    .rpc('get_store_payment_context', { p_org_id: orgId });
                if (!payCtxError) {
                    const ctx = Array.isArray(payCtx) ? (payCtx[0] || null) : (payCtx || null);
                    getnetConfigured = Boolean(ctx?.getnet_configured);
                } else {
                    console.error('Error obteniendo contexto de pago (Getnet):', payCtxError);
                }

                if (!getnetConfigured) {
                    toast.error('Getnet no está configurado en la empresa.');
                    setProcessing(false);
                    return;
                }

                const getnetItems = cartItems.map(item => ({
                    title: buildProductDisplayName(item.name, item.variantName),
                    quantity: item.qty,
                    unit_price: Number(item.price),
                }));
                if (deliveryType === 'envio' && shippingCost > 0) {
                    getnetItems.push({ title: 'Costo de Envio', quantity: 1, unit_price: Number(shippingCost) });
                }

                const returnUrl = `${window.location.origin}/rastreo/${trackingCode}?payment=return`;
                const payerEmail = client?.email || `pedido-${sale.id.slice(0, 8)}@checkout.impulza.com`;

                const { data: edgeData, error: edgeError } = await supabase.functions.invoke('getnet-create-payment', {
                    body: {
                        organizationId: orgId,
                        mode: 'LINK',
                        channel: 'tienda',
                        items: getnetItems,
                        totalAmount: total,
                        externalReference: sale.id,
                        payer: { name: clientName, email: payerEmail },
                        returnUrl,
                    }
                });

                if (edgeData && edgeData.checkout_url) {
                    window.location.href = edgeData.checkout_url;
                    return;
                } else {
                    console.error('Getnet Edge Error:', edgeError || edgeData);
                    toast.error('Error generando el link de pago de Getnet. Probá otro método o más tarde.');
                    setProcessing(false);
                    return;
                }
            }

            onSuccess(trackingCode);

        } catch (err) {
            console.error(err);
            toast.error('Error al procesar el pedido.');
        } finally {
            setProcessing(false);
        }
    };
    const handleDeliverySubmit = (e) => {
        e.preventDefault();

        const normalizedAuth = identityMode === 'document'
            ? normalizeDocument(authIdentifier)
            : normalizePhone(authIdentifier);
        const { nextName, nextPhone, nextAddress, nextCity, nextNotes, nextRequestedDate } = readDeliveryFormValues();
        const normalizedContactPhone = identityMode === 'document'
            ? normalizePhone(nextPhone)
            : normalizePhone(authIdentifier);

        setClientName(nextName);
        setContactPhone(normalizedContactPhone);
        setShippingData((prev) => ({
            ...prev,
            address: nextAddress,
            city: nextCity,
            notes: nextNotes,
            requestedDate: nextRequestedDate,
        }));

        if (identityMode === 'document' && normalizedAuth.length < 7) {
            setStepError('Ingresa un CUIT o DNI valido.');
            toast.error('Ingresa un CUIT o DNI valido');
            return;
        }

        if (identityMode === 'phone' && normalizedAuth.length < 8) {
            setStepError('Ingresa un telefono valido.');
            toast.error('Ingresa un telefono valido');
            return;
        }

        if (identityMode === 'document' && normalizedContactPhone.length < 8) {
            setStepError('Ingresa un telefono de contacto valido.');
            toast.error('Ingresa un telefono de contacto valido');
            return;
        }

        if ((client?.isNew || deliveryType === 'retiro') && !nextName) {
            setStepError('Ingresa el nombre para continuar.');
            toast.error('Ingresa el nombre');
            return;
        }

        if (deliveryType === 'envio' && !nextAddress) {
            setStepError('Ingresa la direccion de envio.');
            toast.error('Ingresa la direccion');
            return;
        }

        setStepError('');
        setStep(2);
    };
    return (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
            <div className="relative w-full max-w-md rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh] animate-in zoom-in-95" style={{ backgroundColor: modalBg, color: modalText, border: `1px solid ${modalBorder}` }}>
                <div className="p-4 border-b flex justify-between items-center" style={{ backgroundColor: accentSoft, borderColor: modalBorder }}>
                    <h3 className="font-bold text-lg" style={{ color: modalText }}>
                        {step === 0 ? 'Identificación' : step === 1 ? 'Entrega' : 'Pago'}
                    </h3>
                    <button onClick={onClose}><X size={20} style={{ color: modalText }} className="opacity-60 hover:opacity-100" /></button>
                </div>

                <div className="p-6 overflow-y-auto">
                    {step === 0 && (
                        matchedClientCandidate ? (
                            <div className="space-y-5">
                                <div className="text-center py-2">
                                    <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: accentSoft, color: accent }}>
                                        <FileText size={26} />
                                    </div>
                                    <h4 className="font-bold" style={{ color: modalText }}>Confirmar cuenta asociada</h4>
                                    <p className="text-xs mt-1 opacity-70" style={{ color: modalText }}>Encontramos una cuenta con ese CUIT o DNI. Confirma si es la correcta antes de seguir.</p>
                                </div>

                                <div className="rounded-2xl border p-4 space-y-2" style={{ borderColor: accentSoftBorder, backgroundColor: accentSoft }}>
                                    <div className="text-[10px] font-bold uppercase tracking-wider" style={{ color: modalText, opacity: 0.65 }}>Cuenta encontrada</div>
                                    <div className="text-sm font-bold" style={{ color: modalText }}>{matchedClientCandidate.full_name || 'Cliente'}</div>
                                    <div className="text-xs" style={{ color: modalText, opacity: 0.75 }}>
                                        {(matchedClientCandidate.doc_type || inferDocumentType(authIdentifier))} {authIdentifier}
                                    </div>
                                    {matchedClientCandidate.phone ? (
                                        <div className="text-xs" style={{ color: modalText, opacity: 0.75 }}>
                                            Telefono: {matchedClientCandidate.phone}
                                        </div>
                                    ) : null}
                                </div>

                                {stepError && (
                                    <div className="text-xs font-medium rounded-xl px-3 py-2 border" style={{ color: '#b91c1c', backgroundColor: '#fef2f2', borderColor: '#fecaca' }}>
                                        {stepError}
                                    </div>
                                )}

                                <button type="button" onClick={handleConfirmMatchedClient} className="w-full py-3 text-white rounded-md font-bold transition-colors" style={{ backgroundColor: accent }}>
                                    Si, es mi cuenta
                                </button>
                                <button type="button" onClick={handleRetryDocument} className="w-full py-3 rounded-md font-bold transition-colors border" style={{ borderColor: modalBorder, color: modalText, backgroundColor: modalBg }}>
                                    No, volver a ingresar CUIT / DNI
                                </button>
                                <button type="button" onClick={handleCreateNewDocumentAccount} className="w-full py-3 rounded-md font-bold transition-colors" style={{ color: accent, backgroundColor: accentSoft }}>
                                    Crear cuenta nueva
                                </button>
                            </div>
                        ) : (
                            <form onSubmit={handleIdentify} className="space-y-5">
                                <div className="text-center py-2">
                                    <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: accentSoft, color: accent }}>
                                        {identityMode === 'document' ? <FileText size={26} /> : <User size={28} />}
                                    </div>
                                    <h4 className="font-bold" style={{ color: modalText }}>
                                        {identityMode === 'document' ? 'Cual es tu CUIT o DNI?' : 'Cual es tu telefono?'}
                                    </h4>
                                    <p className="text-xs mt-1 opacity-70" style={{ color: modalText }}>
                                        {identityMode === 'document'
                                            ? 'Lo usamos para reconocer si ya tienes una cuenta asociada.'
                                            : 'Lo usamos para seguir tu pedido.'}
                                    </p>
                                </div>
                                <input
                                    autoFocus
                                    required
                                    name="auth_identifier"
                                    type="text"
                                    autoComplete={identityMode === 'document' ? 'off' : 'tel'}
                                    inputMode={identityMode === 'document' ? 'numeric' : 'tel'}
                                    className="w-full border rounded-xl px-4 py-3 outline-none text-center font-medium"
                                    style={{ borderColor: modalBorder, color: modalText }}
                                    placeholder={identityMode === 'document' ? 'Ej: 20301234567 o 30123456' : 'Ej: 1155554444'}
                                    value={authIdentifier}
                                    onChange={e => setAuthIdentifier(identityMode === 'document' ? normalizeDocument(e.target.value) : normalizePhone(e.target.value))}
                                    onInput={e => setAuthIdentifier(identityMode === 'document' ? normalizeDocument(e.currentTarget.value) : normalizePhone(e.currentTarget.value))}
                                />
                                {stepError && (
                                    <div className="text-xs font-medium rounded-xl px-3 py-2 border" style={{ color: '#b91c1c', backgroundColor: '#fef2f2', borderColor: '#fecaca' }}>
                                        {stepError}
                                    </div>
                                )}
                                <button type="submit" disabled={processing} className="w-full py-3 text-white rounded-md font-bold transition-colors disabled:opacity-50" style={{ backgroundColor: accent }}>
                                    {processing ? 'Verificando...' : 'Continuar'}
                                </button>
                            </form>
                        )
                    )}
                    {step === 1 && (
                        <form ref={deliveryFormRef} onSubmit={handleDeliverySubmit} className="space-y-4 animate-in fade-in slide-in-from-right-4">
                            {!client?.isNew && deliveryType === 'envio' && (
                                <div className="p-3 rounded-xl flex items-center gap-3 border" style={{ backgroundColor: accentSoft, borderColor: accentSoftBorder }}>
                                    <div className="bg-[var(--color-accent-soft)] text-[var(--color-accent-dark)] w-8 h-8 rounded-full flex items-center justify-center font-bold">OK</div>
                                    <div>
                                        <p className="text-xs text-emerald-800 font-bold">Hola de nuevo, {clientName.split(' ')[0] || 'Cliente'}!</p>
                                        <p className="text-[10px] text-[var(--color-accent-dark)]">Revisa o edita la direccion para este envio.</p>
                                    </div>
                                </div>
                            )}

                            {identityMode === 'document' && (
                                <div className="p-3 rounded-xl border flex items-center gap-3" style={{ backgroundColor: accentSoft, borderColor: accentSoftBorder }}>
                                    <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ backgroundColor: modalBg, color: accent }}>
                                        <FileText size={18} />
                                    </div>
                                    <div>
                                        <p className="text-xs font-bold" style={{ color: modalText }}>
                                            {client?.forceNewWithoutDocument ? 'Cuenta nueva sin asociar este documento' : 'Documento identificado'}
                                        </p>
                                        <p className="text-[10px]" style={{ color: modalText, opacity: 0.7 }}>
                                            {client?.forceNewWithoutDocument ? 'Puedes continuar con un cliente nuevo y completar solo el telefono de contacto.' : `${client?.doc_type || inferDocumentType(authIdentifier)} ${authIdentifier}`}
                                        </p>
                                    </div>
                                </div>
                            )}

                            {(client?.isNew || deliveryType === 'retiro') && (
                                <div className="animate-in fade-in">
                                    <label className="block text-[10px] font-bold uppercase mb-1" style={{ color: modalText, opacity: 0.65 }}>
                                        {deliveryType === 'retiro' ? 'Nombre de quien retira' : 'Tu nombre'}
                                    </label>
                                    <input
                                        name="client_name"
                                        autoComplete="name"
                                        className="w-full border rounded-xl px-3 py-2 outline-none"
                                        style={{ borderColor: modalBorder, color: modalText, backgroundColor: modalBg }}
                                        placeholder="Ej: Juan Perez"
                                        value={clientName}
                                        onChange={e => setClientName(e.target.value)}
                                        onInput={e => setClientName(e.currentTarget.value)}
                                    />
                                </div>
                            )}

                            {identityMode === 'document' && (
                                <div className="animate-in fade-in">
                                    <label className="block text-[10px] font-bold uppercase mb-1" style={{ color: modalText, opacity: 0.65 }}>
                                        Telefono de contacto
                                    </label>
                                    <div className="relative">
                                        <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 opacity-60" style={{ color: modalText }} />
                                        <input
                                            name="contact_phone"
                                            type="tel"
                                            autoComplete="tel"
                                            inputMode="tel"
                                            className="w-full border rounded-xl pl-10 pr-3 py-2 outline-none"
                                            style={{ borderColor: modalBorder, color: modalText, backgroundColor: modalBg }}
                                            placeholder="Ej: 1155554444"
                                            value={contactPhone}
                                            onChange={e => setContactPhone(normalizePhone(e.target.value))}
                                            onInput={e => setContactPhone(normalizePhone(e.currentTarget.value))}
                                        />
                                    </div>
                                </div>
                            )}

                            <div>
                                <label className="block text-[10px] font-bold uppercase mb-2" style={{ color: modalText, opacity: 0.65 }}>Metodo de Entrega</label>
                                <div className="grid grid-cols-2 gap-3">
                                    <button type="button" onClick={() => setDeliveryType('retiro')} className="p-3 rounded-xl border flex flex-col items-center justify-center gap-2 transition-all" style={deliveryType === 'retiro' ? { borderColor: accent, backgroundColor: accentSoft, color: accent } : { borderColor: modalBorder, color: modalText, opacity: 0.65 }}>
                                        <Store size={20} /> <span className="text-xs font-bold">Retiro</span>
                                    </button>
                                    <button type="button" onClick={() => setDeliveryType('envio')} className="p-3 rounded-xl border flex flex-col items-center justify-center gap-2 transition-all" style={deliveryType === 'envio' ? { borderColor: accent, backgroundColor: accentSoft, color: accent } : { borderColor: modalBorder, color: modalText, opacity: 0.65 }}>
                                        <Truck size={20} /> <span className="text-xs font-bold">Envio</span>
                                    </button>
                                </div>
                            </div>

                            {requestDateEnabled && (
                                <div className="space-y-2 rounded-xl border p-4 animate-in fade-in" style={{ backgroundColor: accentSoft, borderColor: accentSoftBorder }}>
                                    <label className="block text-[10px] font-bold uppercase" style={{ color: modalText, opacity: 0.7 }}>
                                        {logisticsConfig.request_date_label || 'Dia del pedido'}
                                    </label>
                                    <input
                                        type="date"
                                        name="requested_date"
                                        min={getLocalToday()}
                                        className="w-full border rounded-lg px-3 py-2 text-sm"
                                        style={{ borderColor: modalBorder, backgroundColor: modalBg, color: modalText }}
                                        value={shippingData.requestedDate}
                                        onChange={e => setShippingData({ ...shippingData, requestedDate: e.target.value })}
                                        onInput={e => setShippingData({ ...shippingData, requestedDate: e.currentTarget.value })}
                                    />
                                    <p className="text-[10px] leading-relaxed" style={{ color: modalText, opacity: 0.65 }}>
                                        {logisticsConfig.request_date_helper || 'Elige el dia en el que quieres retirar o recibir el pedido.'}
                                    </p>
                                </div>
                            )}

                            {deliveryType === 'envio' && config.delivery_estimation?.enabled && (
                                <div className="bg-purple-50 border border-purple-100 p-3 rounded-xl flex items-center gap-3 animate-in fade-in">
                                    <div className="bg-purple-100 text-purple-600 w-8 h-8 rounded-full flex items-center justify-center">
                                        <Timer size={16} />
                                    </div>
                                    <div>
                                        <p className="text-xs text-purple-800 font-bold">Tiempo estimado de entrega</p>
                                        <p className="text-[10px] text-purple-600">
                                            Llega en aproximadamente <span className="font-black">{config.delivery_estimation.time} {config.delivery_estimation.unit}</span>.
                                        </p>
                                    </div>
                                </div>
                            )}

                            {deliveryType === 'envio' && (
                                <div className="space-y-3 p-4 rounded-xl border animate-in fade-in" style={{ backgroundColor: accentSoft, borderColor: accentSoftBorder }}>
                                    <div className="flex items-center gap-2 text-sm font-medium mb-1" style={{ color: modalText }}>
                                        <MapPin size={16} /> Direccion de Envio
                                    </div>

                                    <input
                                        name="shipping_address"
                                        autoComplete="street-address"
                                        className="w-full border rounded-lg px-3 py-2 text-sm"
                                        style={{ borderColor: modalBorder, backgroundColor: modalBg, color: modalText }}
                                        placeholder="Calle y Altura"
                                        value={shippingData.address}
                                        onChange={e => setShippingData({ ...shippingData, address: e.target.value })}
                                        onInput={e => setShippingData({ ...shippingData, address: e.currentTarget.value })}
                                    />
                                    <input
                                        name="shipping_city"
                                        autoComplete="address-level2"
                                        className="w-full border rounded-lg px-3 py-2 text-sm"
                                        style={{ borderColor: modalBorder, backgroundColor: modalBg, color: modalText }}
                                        placeholder="Ciudad / Barrio"
                                        value={shippingData.city}
                                        onChange={e => setShippingData({ ...shippingData, city: e.target.value })}
                                        onInput={e => setShippingData({ ...shippingData, city: e.currentTarget.value })}
                                    />

                                    <div>
                                        <div className="text-[10px] font-bold uppercase mb-1" style={{ color: modalText, opacity: 0.65 }}>Notas (Opcional)</div>
                                        <input
                                            name="shipping_notes"
                                            className="w-full border rounded-lg px-3 py-2 text-sm"
                                            style={{ borderColor: modalBorder, backgroundColor: modalBg, color: modalText }}
                                            placeholder="Ej: Timbre no funciona"
                                            value={shippingData.notes}
                                            onChange={e => setShippingData({ ...shippingData, notes: e.target.value })}
                                            onInput={e => setShippingData({ ...shippingData, notes: e.currentTarget.value })}
                                        />
                                    </div>
                                </div>
                            )}

                            {stepError && (
                                <div className="text-xs font-medium rounded-xl px-3 py-2 border" style={{ color: '#b91c1c', backgroundColor: '#fef2f2', borderColor: '#fecaca' }}>
                                    {stepError}
                                </div>
                            )}

                            <button
                                type="submit"
                                className="w-full py-3 text-white rounded-md font-bold transition-colors mt-2"
                                style={{ backgroundColor: accent }}
                            >
                                Ir a Pagar
                            </button>
                        </form>
                    )}
                    {step === 2 && (
                        <div className="space-y-5 animate-in fade-in slide-in-from-right-4">
                            {lowStockItems.length > 0 && (
                                <LowStockNotice
                                    items={lowStockItems}
                                    message={lowStockAlertConfig.message}
                                    storeConfig={config}
                                />
                            )}
                            <div className="p-4 rounded-xl space-y-2 border" style={{ backgroundColor: accentSoft, borderColor: accentSoftBorder }}>
                                <div className="flex justify-between text-sm" style={{ color: modalText, opacity: 0.8 }}><span>Subtotal</span><span>{formatCurrency(subtotal)}</span></div>
                                {deliveryType === 'envio' && <div className="flex justify-between text-sm" style={{ color: modalText, opacity: 0.8 }}><span>Envío</span><span>{shippingCost === 0 ? 'Gratis' : formatCurrency(shippingCost)}</span></div>}
                                <div className="border-t pt-2 flex justify-between font-black text-lg" style={{ borderColor: accentSoftBorder, color: modalText }}><span>Total</span><span>{formatCurrency(total)}</span></div>
                            </div>

                            <div>
                                <label className="block text-[10px] font-bold uppercase mb-3" style={{ color: modalText, opacity: 0.65 }}>Forma de Pago</label>
                                <div className="space-y-2">
                                    {availableMethods.map(m => {
                                        const isCash = m.name.toLowerCase().includes('efectivo');
                                        const isTransfer = m.name.toLowerCase().includes('transferencia');
                                        
                                        return (
                                        <button 
                                            key={m.id} 
                                            onClick={() => { 
                                                setPaymentMethod(m.id); 
                                                if(!isTransfer) setTransferFile(null); 
                                            }} 
                                            className="w-full p-3 rounded-xl border flex items-center justify-between transition-all"
                                            style={paymentMethod === m.id ? { borderColor: accent, backgroundColor: accentSoft, boxShadow: `0 0 0 1px ${accent}` } : { borderColor: modalBorder, backgroundColor: modalBg }}
                                        >
                                            <div className="flex items-center gap-3">
                                                {isCash ? <Banknote style={{ color: accent }} size={20} /> : (isTransfer ? <QrCode style={{ color: accent }} size={20} /> : <Banknote style={{ color: accent }} size={20} />)}
                                                <span className="font-bold text-sm" style={{ color: modalText }}>{m.display_name || m.name}</span>
                                            </div>
                                            {paymentMethod === m.id && <CheckCircle size={16} style={{ color: accent }} />}
                                        </button>
                                        )
                                    })}
                                </div>
                            </div>

                            {(() => {
                                const selectedMethodObj = availableMethods.find(m => m.id === paymentMethod);
                                const isTransferSelected = selectedMethodObj && selectedMethodObj.name.toLowerCase().includes('transferencia');
                                
                                return isTransferSelected ? (
                                    <div className="animate-in fade-in slide-in-from-top-2">
                                        {config.bank_details && (
                                            <div className="p-3 text-xs rounded-lg border leading-relaxed whitespace-pre-wrap mb-3" style={{ backgroundColor: accentSoft, color: modalText, borderColor: accentSoftBorder }}>
                                                <span className="font-bold block mb-1">Datos Bancarios:</span>
                                                {config.bank_details}
                                            </div>
                                        )}

                                        <div className="border-2 border-dashed rounded-xl p-4 text-center transition-colors relative" style={!transferFile ? { borderColor: accentSoftBorder, backgroundColor: accentSoft } : { borderColor: accent, backgroundColor: accentSoft }}>
                                            <input
                                                type="file"
                                                accept="image/*,.pdf"
                                                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                                onChange={(e) => setTransferFile(e.target.files[0])}
                                            />
                                            <div className="flex flex-col items-center gap-2 text-[var(--text-muted)]">
                                                {transferFile ? (
                                                    <>
                                                        <CheckCircle style={{ color: accent }} size={24} />
                                                        <span className="text-xs font-bold" style={{ color: modalText }}>{transferFile.name}</span>
                                                        <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded" style={{ color: accent, backgroundColor: accentSoft }}>Listo para enviar</span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <UploadCloud size={24} style={{ color: accent }} />
                                                        <span className="text-xs font-bold" style={{ color: accent }}>Adjuntar Comprobante (Requerido)</span>
                                                        <span className="text-[10px]" style={{ color: modalText, opacity: 0.7 }}>Haz clic o arrastra aquí</span>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ) : null;
                            })()}

                            {mpPreferenceId ? (
                                <div className="mt-4 animate-in fade-in slide-in-from-bottom-2 p-2 bg-[var(--surface-2)] rounded-xl" style={{ border: `1px solid ${modalBorder}` }}>
                                    <h4 className="text-center font-bold mb-3 text-[var(--text-default)] text-sm">Completa tu Pago con Mercado Pago</h4>
                                    <Wallet 
                                        initialization={{ preferenceId: mpPreferenceId, redirectMode: 'modal' }}
                                        customization={{ texts: { action: 'pay', valueProp: 'security_safety' } }}
                                        onSubmit={() => onSuccess(currentOrderTracking)}
                                    />
                                    <p className="text-[10px] text-center mt-3 text-[var(--text-muted)]">Paga seguro con tarjeta o saldo en tu cuenta. Gastify recibirá la confirmación automáticamente.</p>
                                    <button onClick={() => onSuccess(currentOrderTracking)} className="w-full text-center text-xs mt-3 text-[var(--text-muted)] underline opacity-70">
                                      O bien, saltar este paso (el pedido quedará pendiente de pago).
                                    </button>
                                </div>
                            ) : (
                                <button
                                    onClick={handleFinish}
                                    disabled={!paymentMethod || processing || (availableMethods.find(m => m.id === paymentMethod)?.name.toLowerCase().includes('transferencia') && !transferFile)}
                                    className="w-full py-4 text-white rounded-md font-bold disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-lg flex items-center justify-center gap-2"
                                    style={{ backgroundColor: accent, boxShadow: `0 10px 25px ${withAlpha(accent, '33')}` }}
                                >
                                    {processing ? <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></span> : <CheckCircle size={20} />}
                                    Confirmar Pedido
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}






